import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, IdCard, Search } from "lucide-react";
import FileUploader from "@/components/FileUploader";
import { useAuth } from "@/context/AuthContext";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const STATUSES = ["Active", "On Leave", "Resigned", "Retired", "Terminated", "Deceased"];
const EMPLOYMENT_TYPES = ["Permanent", "Contract", "Visiting", "Part-time"];
const GENDERS = ["Male", "Female", "Other"];

const empty = {
  employee_code: "",
  name: "",
  gender: "",
  date_of_birth: "",
  designation: "",
  category: "",
  qualification: "",
  specialization: "",
  experience_years: "",
  employment_type: "Permanent",
  status: "Active",
  date_of_joining: "",
  date_of_exit: "",
  date_of_death: "",
  pan: "",
  aadhaar: "",
  mobile: "",
  alt_mobile: "",
  email: "",
  personal_email: "",
  address1: "",
  address2: "",
  city: "",
  state: "",
  pincode: "",
  country: "India",
  emergency_name: "",
  emergency_relation: "",
  emergency_phone: "",
  bank_name: "",
  bank_account_number: "",
  bank_ifsc: "",
  photo_url: "",
  photo_public_id: "",
  pan_url: "",
  pan_public_id: "",
  aadhaar_url: "",
  aadhaar_public_id: "",
  resume_url: "",
  resume_public_id: "",
  joining_letter_url: "",
  joining_letter_public_id: "",
  notes: "",
};

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">
        {label}
      </div>
      {children}
    </label>
  );
}

function SectionHeader({ children }) {
  return (
    <div className="md:col-span-2 pt-2">
      <div className="serif-rule mb-4" />
      <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold mb-2">
        {children}
      </div>
    </div>
  );
}

const StatusPill = ({ s }) => {
  const map = {
    Active: "bg-[#3a5a40]/10 text-[#3a5a40] border-[#3a5a40]/30",
    "On Leave": "bg-[#b07d2c]/10 text-[#b07d2c] border-[#b07d2c]/30",
    Resigned: "bg-[#6b655b]/10 text-[#6b655b] border-[#6b655b]/30",
    Retired: "bg-[#234333]/10 text-[#234333] border-[#234333]/30",
    Terminated: "bg-[#9e2a2b]/10 text-[#9e2a2b] border-[#9e2a2b]/30",
    Deceased: "bg-[#9e2a2b]/10 text-[#9e2a2b] border-[#9e2a2b]/30",
  };
  return (
    <span
      className={`inline-block px-2 py-0.5 text-[10px] tracking-wide uppercase border rounded-sm ${
        map[s] || ""
      }`}
    >
      {s || "—"}
    </span>
  );
};

export default function Employees() {
  const { canWrite } = useAuth();
  const writable = canWrite("employees");
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [qualifications, setQualifications] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  // Filters
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [employmentFilter, setEmploymentFilter] = useState("");

  const fetchAll = async () => {
    const [emps, cats, quals, desigs] = await Promise.all([
      api.get("/employees"),
      api.get("/masters/employee_category"),
      api.get("/masters/qualification"),
      api.get("/masters/designation"),
    ]);
    // Sort by joining date desc (newest first); empty dates last
    const sorted = [...emps.data].sort((a, b) => {
      const da = a.date_of_joining || "";
      const db = b.date_of_joining || "";
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return db.localeCompare(da);
    });
    setItems(sorted);
    setCategories(cats.data);
    setQualifications(quals.data);
    setDesignations(desigs.data);
  };

  useEffect(() => {
    setLoading(true);
    fetchAll().finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((e) => {
      if (statusFilter && e.status !== statusFilter) return false;
      if (categoryFilter && e.category !== categoryFilter) return false;
      if (employmentFilter && e.employment_type !== employmentFilter) return false;
      if (!q) return true;
      return (
        (e.name || "").toLowerCase().includes(q) ||
        (e.employee_code || "").toLowerCase().includes(q) ||
        (e.email || "").toLowerCase().includes(q) ||
        (e.mobile || "").toLowerCase().includes(q) ||
        (e.designation || "").toLowerCase().includes(q)
      );
    });
  }, [items, search, statusFilter, categoryFilter, employmentFilter]);

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };

  const openEdit = (e) => {
    setEditing(e);
    setForm({
      ...empty,
      ...e,
      date_of_birth: e.date_of_birth || "",
      date_of_joining: e.date_of_joining || "",
      date_of_exit: e.date_of_exit || "",
      date_of_death: e.date_of_death || "",
    });
    setOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.employee_code.trim()) {
      toast.error("Employee code is required");
      return;
    }
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (form.status === "Deceased" && !form.date_of_death) {
      toast.error("Date of death is required when status is Deceased");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      date_of_birth: form.date_of_birth || null,
      date_of_joining: form.date_of_joining || null,
      date_of_exit: form.date_of_exit || null,
      date_of_death: form.date_of_death || null,
    };
    try {
      if (editing) {
        const { data } = await api.put(`/employees/${editing.id}`, payload);
        setItems((arr) => arr.map((x) => (x.id === data.id ? data : x)));
        toast.success("Employee updated");
      } else {
        const { data } = await api.post(`/employees`, payload);
        setItems((arr) => [data, ...arr]);
        toast.success("Employee added");
      }
      setOpen(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    const e = confirmDelete;
    setConfirmDelete(null);
    try {
      await api.delete(`/employees/${e.id}`);
      setItems((arr) => arr.filter((x) => x.id !== e.id));
      toast.success("Employee removed");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="p-8 md:p-12 max-w-7xl mx-auto" data-testid="employees-page">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#6b655b]">Master</div>
          <h1 className="mt-2 font-serif text-4xl text-[#1c1a17]">Employees</h1>
          <p className="mt-2 text-sm text-[#6b655b] max-w-2xl">
            Trust-level master roster of teaching, non-teaching and administrative staff with
            identity, contact, salary and document records.
          </p>
        </div>
        <button
          onClick={openCreate}
          data-testid="employee-add-button"
          disabled={!writable}
          title={!writable ? "Read-only — contact a Super Admin for edit access" : undefined}
          className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-6 py-2.5 rounded-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Plus size={14} strokeWidth={1.5} />
          Add Employee
        </button>
      </div>

      {!writable ? (
        <div
          data-testid="employees-readonly-banner"
          className="mb-6 px-4 py-3 border border-[#d4af37]/40 bg-[#d4af37]/5 rounded-sm text-sm text-[#8a6d1d]"
        >
          You have read-only access to Employees. Contact a Super Admin if you need to add or edit records.
        </div>
      ) : null}

      {/* Filters */}
      <div className="bg-white border border-[#e5e0d8] rounded-sm p-4 mb-6 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="relative md:col-span-1">
          <Search
            size={14}
            strokeWidth={1.5}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6b655b]"
          />
          <input
            data-testid="employee-search-input"
            placeholder="Search code, name, email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={inputCls + " pl-9"}
          />
        </div>
        <select
          data-testid="employee-filter-status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className={inputCls}
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select
          data-testid="employee-filter-category"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className={inputCls}
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c.id} value={c.name}>
              {c.name}
            </option>
          ))}
        </select>
        <select
          data-testid="employee-filter-employment"
          value={employmentFilter}
          onChange={(e) => setEmploymentFilter(e.target.value)}
          className={inputCls}
        >
          <option value="">All employment types</option>
          {EMPLOYMENT_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="text-[#6b655b] font-serif text-xl">Loading…</div>
      ) : items.length === 0 ? (
        <div
          className="bg-white border border-dashed border-[#e5e0d8] rounded-sm p-16 text-center"
          data-testid="employees-empty"
        >
          <IdCard size={32} strokeWidth={1.5} className="mx-auto text-[#234333]" />
          <div className="mt-4 font-serif text-2xl text-[#1c1a17]">No employees on record</div>
          <div className="mt-2 text-sm text-[#6b655b]">Add the first employee to get started.</div>
          {writable ? (
            <button
              onClick={openCreate}
              data-testid="employees-empty-add-button"
              className="mt-6 inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-6 py-2.5 rounded-sm"
            >
              <Plus size={14} strokeWidth={1.5} /> Add the first employee
            </button>
          ) : null}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-[#e5e0d8] rounded-sm p-12 text-center text-sm text-[#6b655b]">
          No employees match the current filters.
        </div>
      ) : (
        <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="employees-table">
            <thead>
              <tr className="bg-[#f9f8f6] text-left">
                {[
                  "",
                  "Code",
                  "Name",
                  "Designation",
                  "Category",
                  "Type",
                  "Documents",
                  "Joined",
                  "Status",
                  "",
                ].map((h, i) => (
                  <th
                    key={i}
                    className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr
                  key={e.id}
                  className="border-t border-[#e5e0d8] hover:bg-[#f9f8f6]/60"
                  data-testid={`employee-row-${e.id}`}
                >
                  <td className="px-4 py-4">
                    {e.photo_url ? (
                      <img
                        src={e.photo_url}
                        alt={e.name}
                        className="w-9 h-9 rounded-full object-cover border border-[#e5e0d8]"
                      />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-[#f9f8f6] border border-[#e5e0d8] flex items-center justify-center font-serif text-[#234333] text-sm">
                        {(e.name || "?").charAt(0).toUpperCase()}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 text-[#1c1a17] font-mono text-xs">{e.employee_code}</td>
                  <td className="px-4 py-4 text-[#1c1a17] whitespace-nowrap">
                    {e.name}
                    {e.email ? (
                      <div className="text-[11px] text-[#6b655b]">{e.email}</div>
                    ) : null}
                  </td>
                  <td className="px-4 py-4 text-[#6b655b]">{e.designation || "—"}</td>
                  <td className="px-4 py-4 text-[#6b655b]">{e.category || "—"}</td>
                  <td className="px-4 py-4 text-[#6b655b] whitespace-nowrap">
                    {e.employment_type || "—"}
                  </td>
                  <td className="px-4 py-4 text-[#6b655b]">
                    <div className="flex flex-wrap gap-1.5 text-[10px]">
                      {[
                        ["pan_url", "PAN"],
                        ["aadhaar_url", "AADHAAR"],
                        ["resume_url", "RESUME"],
                        ["joining_letter_url", "JOINING"],
                      ].map(([key, lbl]) =>
                        e[key] ? (
                          <a
                            key={key}
                            href={e[key]}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-2 py-0.5 border border-[#234333]/30 text-[#234333] rounded-sm hover:bg-[#efece6]"
                          >
                            {lbl}
                          </a>
                        ) : null,
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-[#6b655b] whitespace-nowrap">
                    {e.date_of_joining || "—"}
                  </td>
                  <td className="px-4 py-4">
                    <StatusPill s={e.status} />
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-right">
                    <button
                      onClick={() => openEdit(e)}
                      data-testid={`employee-edit-${e.id}`}
                      className="inline-flex items-center gap-1 text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm"
                    >
                      <Pencil size={14} strokeWidth={1.5} /> {writable ? "Edit" : "View"}
                    </button>
                    {writable ? (
                      <button
                        onClick={() => setConfirmDelete(e)}
                        data-testid={`employee-delete-${e.id}`}
                        className="ml-2 inline-flex items-center gap-1 text-[#9e2a2b] hover:bg-[#9e2a2b]/5 px-2 py-1 rounded-sm"
                      >
                        <Trash2 size={14} strokeWidth={1.5} /> Delete
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-white border-[#e5e0d8] rounded-sm max-w-4xl max-h-[90vh] overflow-y-auto"
          data-testid="employee-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#1c1a17]">
              {editing ? "Edit Employee" : "Add Employee"}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
            {/* Identity */}
            <SectionHeader>Identity</SectionHeader>
            <Field label="Employee Code *">
              <input
                data-testid="employee-code-input"
                className={inputCls}
                value={form.employee_code}
                onChange={(e) => set("employee_code", e.target.value)}
              />
            </Field>
            <Field label="Full Name *">
              <input
                data-testid="employee-name-input"
                className={inputCls}
                value={form.name}
                onChange={(e) => set("name", e.target.value)}
              />
            </Field>
            <Field label="Gender">
              <select
                data-testid="employee-gender-select"
                className={inputCls}
                value={form.gender}
                onChange={(e) => set("gender", e.target.value)}
              >
                <option value="">— Select —</option>
                {GENDERS.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Date of Birth">
              <input
                data-testid="employee-dob-input"
                type="date"
                className={inputCls}
                value={form.date_of_birth}
                onChange={(e) => set("date_of_birth", e.target.value)}
              />
            </Field>
            <Field label="PAN">
              <input
                data-testid="employee-pan-input"
                className={inputCls}
                value={form.pan}
                onChange={(e) => set("pan", e.target.value.toUpperCase())}
              />
            </Field>
            <Field label="Aadhaar">
              <input
                data-testid="employee-aadhaar-input"
                className={inputCls}
                value={form.aadhaar}
                onChange={(e) => set("aadhaar", e.target.value)}
              />
            </Field>

            {/* Employment */}
            <SectionHeader>Employment</SectionHeader>
            <Field label="Designation">
              <select
                data-testid="employee-designation-select"
                className={inputCls}
                value={form.designation}
                onChange={(e) => set("designation", e.target.value)}
              >
                <option value="">— Select —</option>
                {designations.map((d) => (
                  <option key={d.id} value={d.name}>
                    {d.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Category">
              <select
                data-testid="employee-category-select"
                className={inputCls}
                value={form.category}
                onChange={(e) => set("category", e.target.value)}
              >
                <option value="">— Select —</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.name}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Qualification">
              <select
                data-testid="employee-qualification-select"
                className={inputCls}
                value={form.qualification}
                onChange={(e) => set("qualification", e.target.value)}
              >
                <option value="">— Select —</option>
                {qualifications.map((q) => (
                  <option key={q.id} value={q.name}>
                    {q.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Specialization">
              <input
                data-testid="employee-specialization-input"
                className={inputCls}
                value={form.specialization}
                onChange={(e) => set("specialization", e.target.value)}
              />
            </Field>
            <Field label="Experience (years)">
              <input
                data-testid="employee-experience-input"
                className={inputCls}
                value={form.experience_years}
                onChange={(e) => set("experience_years", e.target.value)}
              />
            </Field>
            <Field label="Employment Type">
              <select
                data-testid="employee-employment-type-select"
                className={inputCls}
                value={form.employment_type}
                onChange={(e) => set("employment_type", e.target.value)}
              >
                {EMPLOYMENT_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Status">
              <select
                data-testid="employee-status-select"
                className={inputCls}
                value={form.status}
                onChange={(e) => set("status", e.target.value)}
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Date of Joining">
              <input
                data-testid="employee-doj-input"
                type="date"
                className={inputCls}
                value={form.date_of_joining}
                onChange={(e) => set("date_of_joining", e.target.value)}
              />
            </Field>
            <Field label="Date of Exit">
              <input
                data-testid="employee-doe-input"
                type="date"
                className={inputCls}
                value={form.date_of_exit}
                onChange={(e) => set("date_of_exit", e.target.value)}
              />
            </Field>
            {form.status === "Deceased" && (
              <Field label="Date of Death *">
                <input
                  data-testid="employee-dod-input"
                  type="date"
                  className={inputCls}
                  value={form.date_of_death}
                  onChange={(e) => set("date_of_death", e.target.value)}
                />
              </Field>
            )}

            {/* Contact */}
            <SectionHeader>Contact</SectionHeader>
            <Field label="Mobile Number">
              <input
                data-testid="employee-mobile-input"
                className={inputCls}
                value={form.mobile}
                onChange={(e) => set("mobile", e.target.value)}
              />
            </Field>
            <Field label="Alternate Mobile">
              <input
                data-testid="employee-alt-mobile-input"
                className={inputCls}
                value={form.alt_mobile}
                onChange={(e) => set("alt_mobile", e.target.value)}
              />
            </Field>
            <Field label="Work Email">
              <input
                data-testid="employee-email-input"
                type="email"
                className={inputCls}
                value={form.email}
                onChange={(e) => set("email", e.target.value)}
              />
            </Field>
            <Field label="Personal Email">
              <input
                data-testid="employee-personal-email-input"
                type="email"
                className={inputCls}
                value={form.personal_email}
                onChange={(e) => set("personal_email", e.target.value)}
              />
            </Field>
            <Field label="Address Line 1" full>
              <input
                data-testid="employee-address1-input"
                className={inputCls}
                value={form.address1}
                onChange={(e) => set("address1", e.target.value)}
              />
            </Field>
            <Field label="Address Line 2" full>
              <input
                data-testid="employee-address2-input"
                className={inputCls}
                value={form.address2}
                onChange={(e) => set("address2", e.target.value)}
              />
            </Field>
            <Field label="City">
              <input
                data-testid="employee-city-input"
                className={inputCls}
                value={form.city}
                onChange={(e) => set("city", e.target.value)}
              />
            </Field>
            <Field label="State">
              <input
                data-testid="employee-state-input"
                className={inputCls}
                value={form.state}
                onChange={(e) => set("state", e.target.value)}
              />
            </Field>
            <Field label="PIN Code">
              <input
                data-testid="employee-pincode-input"
                className={inputCls}
                value={form.pincode}
                onChange={(e) => set("pincode", e.target.value)}
              />
            </Field>
            <Field label="Country">
              <input
                data-testid="employee-country-input"
                className={inputCls}
                value={form.country}
                onChange={(e) => set("country", e.target.value)}
              />
            </Field>
            <Field label="Emergency Contact Name">
              <input
                data-testid="employee-emergency-name-input"
                className={inputCls}
                value={form.emergency_name}
                onChange={(e) => set("emergency_name", e.target.value)}
              />
            </Field>
            <Field label="Relationship">
              <input
                data-testid="employee-emergency-relation-input"
                className={inputCls}
                value={form.emergency_relation}
                onChange={(e) => set("emergency_relation", e.target.value)}
              />
            </Field>
            <Field label="Emergency Phone" full>
              <input
                data-testid="employee-emergency-phone-input"
                className={inputCls}
                value={form.emergency_phone}
                onChange={(e) => set("emergency_phone", e.target.value)}
              />
            </Field>

            {/* Salary / Bank */}
            <SectionHeader>Salary Bank Account</SectionHeader>
            <Field label="Bank Name">
              <input
                data-testid="employee-bank-name-input"
                className={inputCls}
                value={form.bank_name}
                onChange={(e) => set("bank_name", e.target.value)}
              />
            </Field>
            <Field label="Account Number">
              <input
                data-testid="employee-bank-account-input"
                className={inputCls}
                value={form.bank_account_number}
                onChange={(e) => set("bank_account_number", e.target.value)}
              />
            </Field>
            <Field label="IFSC" full>
              <input
                data-testid="employee-bank-ifsc-input"
                className={inputCls}
                value={form.bank_ifsc}
                onChange={(e) => set("bank_ifsc", e.target.value.toUpperCase())}
              />
            </Field>

            {/* Documents */}
            <SectionHeader>Documents</SectionHeader>
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-5">
              <FileUploader
                kind="photo"
                label="Photo"
                url={form.photo_url}
                testid="employee-photo"
                onChange={({ url, public_id }) =>
                  setForm((f) => ({ ...f, photo_url: url, photo_public_id: public_id }))
                }
              />
              <FileUploader
                kind="pan"
                label="PAN Card"
                url={form.pan_url}
                testid="employee-pan-doc"
                onChange={({ url, public_id }) =>
                  setForm((f) => ({ ...f, pan_url: url, pan_public_id: public_id }))
                }
              />
              <FileUploader
                kind="aadhaar"
                label="Aadhaar Card"
                url={form.aadhaar_url}
                testid="employee-aadhaar-doc"
                onChange={({ url, public_id }) =>
                  setForm((f) => ({ ...f, aadhaar_url: url, aadhaar_public_id: public_id }))
                }
              />
              <FileUploader
                kind="employee_document"
                label="Resume"
                url={form.resume_url}
                testid="employee-resume-doc"
                onChange={({ url, public_id }) =>
                  setForm((f) => ({ ...f, resume_url: url, resume_public_id: public_id }))
                }
              />
              <FileUploader
                kind="employee_document"
                label="Joining Letter"
                url={form.joining_letter_url}
                testid="employee-joining-letter-doc"
                onChange={({ url, public_id }) =>
                  setForm((f) => ({
                    ...f,
                    joining_letter_url: url,
                    joining_letter_public_id: public_id,
                  }))
                }
              />
            </div>

            <Field label="Notes" full>
              <textarea
                data-testid="employee-notes-input"
                className={inputCls + " min-h-[80px]"}
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
              />
            </Field>
          </div>

          <DialogFooter className="pt-4">
            <button
              onClick={() => setOpen(false)}
              data-testid="employee-cancel-button"
              className="bg-white hover:bg-[#f9f8f6] text-[#1c1a17] border border-[#e5e0d8] text-sm px-5 py-2 rounded-sm"
            >
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="employee-save-button"
              className="bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm disabled:opacity-60"
            >
              {saving ? "Saving…" : editing ? "Update" : "Create"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
      >
        <AlertDialogContent className="bg-white border-[#e5e0d8] rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-[#1c1a17]">
              Remove this employee?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6b655b]">
              {confirmDelete?.name} ({confirmDelete?.employee_code}) will be permanently removed
              along with all uploaded documents.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="employee-delete-cancel"
              className="bg-white text-[#1c1a17] border-[#e5e0d8] rounded-sm"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              data-testid="employee-delete-confirm"
              className="bg-[#9e2a2b] hover:bg-[#7d2122] text-white rounded-sm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
